/**
 * Display-side GST/HST rates by the dental office's province.
 *
 * Mirrors the server's rate table in tempstars-api `src/services/tax.service.ts`
 * (the server remains the source of truth for what is actually charged) — keep
 * the two in sync until a tax-quote endpoint exists.
 *
 * Provincial taxes (QST, BC/SK/MB PST) are intentionally excluded until
 * TempStars registers in those provinces.
 */

export interface SalesTaxInfo {
    ratePct: number;
    label: string | null;
}

const HST_RATES: { [province: string]: number } = {
    ON: 13,
    NB: 15,
    NL: 15,
    PE: 15,
    NS: 14, // reduced from 15% effective April 1, 2025
};

const GST_PROVINCES = ['AB', 'BC', 'MB', 'QC', 'SK', 'NT', 'NU', 'YT'];
const GST_RATE_PCT = 5;

// Some data sources store the full province name rather than the 2-letter code.
const PROVINCE_NAME_TO_CODE: { [name: string]: string } = {
    'ONTARIO': 'ON',
    'BRITISH COLUMBIA': 'BC',
    'ALBERTA': 'AB',
    'MANITOBA': 'MB',
    'SASKATCHEWAN': 'SK',
    'QUEBEC': 'QC',
    'QUÉBEC': 'QC',
    'NEW BRUNSWICK': 'NB',
    'NOVA SCOTIA': 'NS',
    'PRINCE EDWARD ISLAND': 'PE',
    'NEWFOUNDLAND AND LABRADOR': 'NL',
    'NORTHWEST TERRITORIES': 'NT',
    'NUNAVUT': 'NU',
    'YUKON': 'YT',
};

export function getSalesTaxInfo(country?: number | string | null, province?: string | null): SalesTaxInfo {
    const isUS = country === 1 || country === '1' || country === 'US';
    if (isUS) {
        return { ratePct: 0, label: null };
    }

    const prov = String(province || '').trim().toUpperCase();
    const normalized = PROVINCE_NAME_TO_CODE[prov] || prov;

    if (HST_RATES[normalized] !== undefined) {
        const ratePct = HST_RATES[normalized];
        return { ratePct, label: `HST (${ratePct}%)` };
    }

    if (GST_PROVINCES.indexOf(normalized) !== -1) {
        return { ratePct: GST_RATE_PCT, label: `GST (${GST_RATE_PCT}%)` };
    }

    // Unknown/blank Canadian province: keep the legacy Ontario treatment (13% HST).
    return { ratePct: 13, label: 'HST (13%)' };
}
